import { z } from 'zod';

import { FeatureFlag, Tier } from '../schema/features.js';
import { TenantId, Uuid } from '../schema/primitives.js';

/** Identifies which signing key produced a signature (see key rotation). */
export const KeyId = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9._-]+$/);
export type KeyId = z.infer<typeof KeyId>;

/**
 * License — Ed25519-signed JSON object granting a tier (and optionally
 * specific feature grants) to a tenant. Verification is fully offline.
 *
 * The signature is hex-encoded Ed25519 (128 chars, 64 bytes) over the
 * canonical JSON of all OTHER fields. `expiresAt` omitted = perpetual.
 */
export const License = z.object({
  /**
   * Stable license id (a UUID / jti). Lets a SPECIFIC license be revoked (see
   * the signed revocation list) without disabling the whole tenant. Signed.
   * Optional for back-compat — pre-id licenses can't be individually revoked,
   * but the tenant-level online revocation (fetch → 404 → Core) still applies.
   */
  id: Uuid.optional(),
  /**
   * Which signing key signed this license (key rotation). The verifier resolves
   * it against a pinned keyring; absent → the default/legacy key. Signed, so it
   * can't be swapped to point at a different key.
   */
  keyId: KeyId.optional(),
  tenantId: TenantId,
  tier: Tier,
  features: z.array(FeatureFlag),
  expiresAt: z.number().int().nonnegative().optional(),
  /**
   * Offline grace window in days, honored AFTER `expiresAt`: the license stays
   * valid until `expiresAt + graceDays·86400`, so a fleet that can't reach the
   * licensing server the day its term ends degrades on a ramp (with a
   * diagnostic warning) rather than a hard cliff. Signed, so a tenant can't
   * self-extend. Omitted = no grace. Ignored when `expiresAt` is absent
   * (perpetual). Capped at a year.
   */
  graceDays: z.number().int().nonnegative().max(365).optional(),
  signature: z
    .string()
    .length(128)
    .regex(/^[0-9a-f]{128}$/, 'signature must be 128 lowercase hex chars'),
});
export type License = z.infer<typeof License>;

/** Same as License minus the signature; what `signLicense` accepts. */
export const UnsignedLicense = License.omit({ signature: true });
export type UnsignedLicense = z.infer<typeof UnsignedLicense>;
